import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Select } from '../components/ui/Select';
import { getZonalAccess } from '../utils/permissions';
import { zonaStatsService, IndicadoresFinancieros, IndicadoresOperativos, FiltrosZona, HierarchicalStats } from '../services/zonaStatsService';
import { provinciaEclesiasticaService, ProvinciaEclesiastica } from '../services/provinciaEclesiasticaService';
import { diocesisService, Diocesis } from '../services/diocesisService';
import { usuarioService } from '../services/usuarioService';
import { solicitudRolService } from '../services/solicitudRolService';
import { DollarSign, TrendingUp, Users, Building2, Briefcase, Megaphone, ArrowUpCircle, ArrowDownCircle, UserCheck, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';

const GRID_COLS = '1fr 80px 100px 100px 100px';

export default function IndicadoresPage() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const zonalAccess = getZonalAccess(usuario);

  const [loading, setLoading] = useState(true);
  const [indicadoresFinancieros, setIndicadoresFinancieros] = useState<IndicadoresFinancieros | null>(null);
  const [indicadoresOperativos, setIndicadoresOperativos] = useState<IndicadoresOperativos | null>(null);
  const [hierarchicalStats, setHierarchicalStats] = useState<HierarchicalStats>({ provincias: [] });
  const [totalUsuarios, setTotalUsuarios] = useState<number>(0);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState<number>(0);
  const [expandedProvincias, setExpandedProvincias] = useState<Set<string>>(new Set());
  const [expandedDiocesis, setExpandedDiocesis] = useState<Set<string>>(new Set());

  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [diocesis, setDiocesis] = useState<Diocesis[]>([]);

  const [filtros, setFiltros] = useState<FiltrosZona>({
    provinciaEclesiasticaId: zonalAccess.provinciaEclesiasticaId || undefined,
    diocesisId: zonalAccess.diocesisId || undefined,
  });

  useEffect(() => {
    loadProvinciasEclesiasticas();
    if (filtros.provinciaEclesiasticaId) {
      loadDiocesis(filtros.provinciaEclesiasticaId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const loadProvinciasEclesiasticas = async () => {
    try {
      const data = await provinciaEclesiasticaService.getAll();
      setProvinciasEclesiasticas(data);
    } catch (error) {
      console.error('Error cargando provincias eclesiásticas:', error);
    }
  };

  const loadDiocesis = async (provinciaEclesiasticaId: string) => {
    try {
      const data = await diocesisService.getByProvinciaEclesiastica(provinciaEclesiasticaId);
      setDiocesis(data);
    } catch (error) {
      console.error('Error cargando diócesis:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [financieros, operativos, , hierarchical, usuarios, solicitudes] = await Promise.all([
        zonaStatsService.getIndicadoresFinancieros(filtros),
        zonaStatsService.getIndicadoresOperativos(filtros),
        zonaStatsService.getLumisialesStats(filtros),
        zonaStatsService.getLumisialesStatsGrouped(filtros),
        usuarioService.getTotalActivos(),
        solicitudRolService.getCountPendientes(),
      ]);

      setIndicadoresFinancieros(financieros);
      setIndicadoresOperativos(operativos);
      setHierarchicalStats(hierarchical);
      setTotalUsuarios(usuarios);
      setSolicitudesPendientes(solicitudes);
    } catch (error) {
      console.error('Error cargando indicadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProvincia = (provinciaId: string) => {
    setExpandedProvincias(prev => {
      const newSet = new Set(prev);
      if (newSet.has(provinciaId)) {
        newSet.delete(provinciaId);
      } else {
        newSet.add(provinciaId);
      }
      return newSet;
    });
  };

  const toggleDiocesis = (diocesisId: string) => {
    setExpandedDiocesis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(diocesisId)) {
        newSet.delete(diocesisId);
      } else {
        newSet.add(diocesisId);
      }
      return newSet;
    });
  };

  const handleProvinciaEclesiasticaChange = (provinciaEclesiasticaId: string) => {
    const newProvinciaEclesiasticaId = provinciaEclesiasticaId || undefined;
    setFiltros({
      ...filtros,
      provinciaEclesiasticaId: newProvinciaEclesiasticaId,
      diocesisId: undefined,
    });
    if (newProvinciaEclesiasticaId) {
      loadDiocesis(newProvinciaEclesiasticaId);
    } else {
      setDiocesis([]);
    }
  };

  const handleDiocesisChange = (diocesisId: string) => {
    setFiltros({
      ...filtros,
      diocesisId: diocesisId || undefined,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Indicadores
          </h1>
          <p className="text-slate-600">
            Vista consolidada de métricas de tu zona
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Indicadores de Usuarios</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <UserCheck className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Usuarios Activos</p>
            <p className="text-3xl font-bold">{totalUsuarios}</p>
          </div>

          <div
            onClick={() => navigate('/admin/usuarios', { state: { activeTab: 'pendientes' } })}
            className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8" />
              {solicitudesPendientes > 0 && (
                <span className="bg-white text-amber-600 text-xs font-bold px-2 py-1 rounded-full">
                  {solicitudesPendientes}
                </span>
              )}
            </div>
            <p className="text-sm opacity-90 mb-1">Solicitudes Pendientes</p>
            <p className="text-3xl font-bold">{solicitudesPendientes}</p>
            <p className="text-xs opacity-75 mt-2">Haz clic para gestionar</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Filtros Zonales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {!zonalAccess.provinciaEclesiasticaId && (
            <div>
              <Select
                label="Provincia Eclesiástica"
                options={provinciasEclesiasticas.map((provincia) => ({ value: provincia.id, label: provincia.nombre }))}
                value={filtros.provinciaEclesiasticaId || ''}
                onChange={(v) => handleProvinciaEclesiasticaChange(v)}
                placeholder="Todas las provincias eclesiásticas"
              />
            </div>
          )}

          {!zonalAccess.diocesisId && filtros.provinciaEclesiasticaId && (
            <div>
              <Select
                label="Diócesis"
                options={diocesis.map((d) => ({ value: d.id, label: d.nombre }))}
                value={filtros.diocesisId || ''}
                onChange={(v) => handleDiocesisChange(v)}
                placeholder="Todas las diócesis"
              />
            </div>
          )}

          <div>
            <label htmlFor="filtro-ciudad" className="block text-sm font-medium text-slate-700 mb-1">Ciudad</label>
            <input
              id="filtro-ciudad"
              type="text"
              value={filtros.ciudad || ''}
              onChange={(e) => setFiltros({ ...filtros, ciudad: e.target.value || undefined })}
              placeholder="Filtrar por ciudad"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>

          <div>
            <label htmlFor="filtro-region" className="block text-sm font-medium text-slate-700 mb-1">Región</label>
            <input
              id="filtro-region"
              type="text"
              value={filtros.region || ''}
              onChange={(e) => setFiltros({ ...filtros, region: e.target.value || undefined })}
              placeholder="Filtrar por región"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Indicadores Financieros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Total Aportes</p>
            <p className="text-3xl font-bold">{formatCurrency(indicadoresFinancieros?.totalAportes || 0)}</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Balance Agregado</p>
            <p className="text-3xl font-bold">{formatCurrency(indicadoresFinancieros?.balanceAgregado || 0)}</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <ArrowUpCircle className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Total Ingresos</p>
            <p className="text-3xl font-bold">{formatCurrency(indicadoresFinancieros?.totalIngresos || 0)}</p>
            <p className="text-xs opacity-75 mt-1">Sin cuota iglesia</p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Cuota Iglesia</p>
            <p className="text-3xl font-bold">{formatCurrency(indicadoresFinancieros?.totalCuotaIglesiaRecaudada || 0)}</p>
            <p className="text-xs opacity-75 mt-1">No suma al balance</p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <ArrowDownCircle className="w-8 h-8" />
            </div>
            <p className="text-sm opacity-90 mb-1">Total Egresos</p>
            <p className="text-3xl font-bold">{formatCurrency(indicadoresFinancieros?.totalEgresos || 0)}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Indicadores Operativos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6 text-slate-600" />
              <h3 className="font-semibold text-slate-700">Lumisiales Activos</h3>
            </div>
            <p className="text-4xl font-bold text-slate-900">{indicadoresOperativos?.totalLumisiales || 0}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-slate-600" />
              <h3 className="font-semibold text-slate-700">Miembros Activos</h3>
            </div>
            <p className="text-4xl font-bold text-slate-900">{indicadoresOperativos?.totalMiembrosActivos || 0}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Megaphone className="w-6 h-6 text-slate-600" />
              <h3 className="font-semibold text-slate-700">Campañas Activas</h3>
            </div>
            <p className="text-4xl font-bold text-slate-900">{indicadoresOperativos?.campanasActivas || 0}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="w-6 h-6 text-slate-600" />
              <h3 className="font-semibold text-slate-700">Actividades Activas</h3>
            </div>
            <p className="text-4xl font-bold text-slate-900">{indicadoresOperativos?.actividadesEconomicasActivas || 0}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Desglose por Lumisial</h2>
        <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
          {hierarchicalStats.provincias.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No se encontraron Lumisiales
            </div>
          ) : (
            <div
              className="grid"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              <div className="contents">
                <div
                  className="col-span-full grid border-b border-gray-200 bg-gray-50"
                  style={{ gridTemplateColumns: GRID_COLS }}
                >
                  <div className="py-2.5 pl-4" />
                  <div className="py-2.5 text-center">
                    <span className="text-xs font-medium text-slate-500 flex items-center justify-center gap-1">
                      <Users className="w-3 h-3" /> Miembros
                    </span>
                  </div>
                  <div className="py-2.5 text-center">
                    <span className="text-xs font-medium text-slate-500 flex items-center justify-center gap-1">
                      <DollarSign className="w-3 h-3" /> Balance
                    </span>
                  </div>
                  <div className="py-2.5 text-center">
                    <span className="text-xs font-medium text-slate-500 flex items-center justify-center gap-1">
                      <ArrowUpCircle className="w-3 h-3" /> Ingresos
                    </span>
                  </div>
                  <div className="py-2.5 pr-4 text-center">
                    <span className="text-xs font-medium text-slate-500 flex items-center justify-center gap-1">
                      <ArrowDownCircle className="w-3 h-3" /> Egresos
                    </span>
                  </div>
                </div>
              </div>

              {hierarchicalStats.provincias.map((provincia) => (
                <div key={provincia.provinciaId} className="contents">
                  <button
                    onClick={() => toggleProvincia(provincia.provinciaId)}
                    className="col-span-full grid hover:bg-gray-50 transition-colors border-t border-gray-200 first:border-t-0"
                    style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
                  >
                    <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
                      {expandedProvincias.has(provincia.provinciaId)
                        ? <ChevronDown className="w-5 h-5 text-slate-600 flex-shrink-0" />
                        : <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                      }
                      <span className="font-bold text-slate-800 truncate">{provincia.provinciaNombre}</span>
                    </div>
                    <div className="text-center py-3 pr-2">
                      <span className="font-bold text-slate-900">{provincia.miembrosActivos}</span>
                    </div>
                    <div className="text-center py-3 pr-2">
                      <span className={`font-bold ${provincia.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(provincia.balance)}
                      </span>
                    </div>
                    <div className="text-center py-3 pr-2">
                      <span className="font-bold text-slate-900">{formatCurrency(provincia.ingresos)}</span>
                    </div>
                    <div className="text-center py-3 pr-4">
                      <span className="font-bold text-slate-900">{formatCurrency(provincia.egresos)}</span>
                    </div>
                  </button>

                  {expandedProvincias.has(provincia.provinciaId) && provincia.diocesis.map((diocesis) => (
                    <div key={diocesis.diocesisId} className="contents">
                      <button
                        onClick={() => toggleDiocesis(diocesis.diocesisId)}
                        className="col-span-full grid hover:bg-gray-50 transition-colors border-t border-gray-100"
                        style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
                      >
                        <div className="flex items-center gap-2 py-2.5 pl-10 min-w-0">
                          {expandedDiocesis.has(diocesis.diocesisId)
                            ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          }
                          <span className="font-semibold text-slate-700 text-sm truncate">
                            {diocesis.diocesisTipo === 'diocesis' ? 'Diócesis' : 'Prefectura'} {diocesis.diocesisNombre}
                          </span>
                        </div>
                        <div className="text-center py-2.5 pr-2">
                          <span className="font-semibold text-slate-800 text-sm">{diocesis.miembrosActivos}</span>
                        </div>
                        <div className="text-center py-2.5 pr-2">
                          <span className={`font-semibold text-sm ${diocesis.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(diocesis.balance)}
                          </span>
                        </div>
                        <div className="text-center py-2.5 pr-2">
                          <span className="font-semibold text-slate-800 text-sm">{formatCurrency(diocesis.ingresos)}</span>
                        </div>
                        <div className="text-center py-2.5 pr-4">
                          <span className="font-semibold text-slate-800 text-sm">{formatCurrency(diocesis.egresos)}</span>
                        </div>
                      </button>

                      {expandedDiocesis.has(diocesis.diocesisId) && diocesis.lumisiales.map((lumisial) => (
                        <div key={lumisial.id} className="contents">
                          <div
                            className="col-span-full grid hover:bg-gray-50 transition-colors border-t border-gray-100"
                            style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
                          >
                            <div className="py-2.5 pl-16 min-w-0">
                              <p className="font-medium text-slate-800 text-sm truncate">{lumisial.nombre}</p>
                              {lumisial.ciudad && (
                                <p className="text-xs text-slate-400 truncate">{lumisial.ciudad}</p>
                              )}
                            </div>
                            <div className="text-center py-2.5 pr-2">
                              <span className="text-sm font-semibold text-slate-800">{lumisial.miembrosActivos}</span>
                            </div>
                            <div className="text-center py-2.5 pr-2">
                              <span className={`text-sm font-semibold ${lumisial.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(lumisial.balance)}
                              </span>
                            </div>
                            <div className="text-center py-2.5 pr-2">
                              <span className="text-sm font-semibold text-slate-800">{formatCurrency(lumisial.ingresos)}</span>
                            </div>
                            <div className="text-center py-2.5 pr-4">
                              <span className="text-sm font-semibold text-slate-800">{formatCurrency(lumisial.egresos)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
